import { getLocalizedPath, type Locale } from "@/shared/i18n";
import type {
  DownloadPageData,
  DownloadProduct,
  DownloadProductCategory,
  DownloadResourceCategory,
  DownloadResourcePost,
  FireflyApiResponse,
} from "./download-types";

const defaultFireflyApiBaseUrl = "https://community.t-firefly.com/api";
const fireflyApiBaseUrl =
  process.env.FIREFLY_API_BASE_URL ?? defaultFireflyApiBaseUrl;

/**
 * 获取资料下载中心入口路径。
 * @param locale 当前语言。
 * @returns 当前语言下的下载中心入口路径。
 */
export function getDownloadIndexPath(locale: Locale) {
  return getLocalizedPath("/doc/download", locale);
}

/**
 * 获取指定产品的资料下载路径。
 * @param locale 当前语言。
 * @param productId 产品 ID。
 * @returns 当前语言下的产品下载页路径。
 */
export function getDownloadProductPath(
  locale: Locale,
  productId: number | string,
) {
  return getLocalizedPath(`/doc/download/${productId}`, locale);
}

/**
 * 加载下载中心入口页数据，并根据左侧目录顺序选择首个产品。
 * @param options 加载参数。
 * @param options.locale 当前语言。
 * @returns 入口页展示所需的数据和错误信息。
 */
export async function loadDownloadIndexPageData(options: {
  locale: Locale;
}): Promise<DownloadPageData> {
  const [productCategoriesResult] = await Promise.allSettled([
    fetchDownloadProductCategories(options.locale),
  ]);
  const productCategories =
    productCategoriesResult.status === "fulfilled"
      ? asArray<DownloadProductCategory>(productCategoriesResult.value)
      : [];
  const firstProduct = findFirstDownloadProduct(productCategories);

  if (!firstProduct) {
    return buildDownloadPageData({
      productCategoriesResult,
      productId: undefined,
      resourceCategoriesResult: {
        status: "fulfilled",
        value: [],
      },
    });
  }

  const [resourceCategoriesResult] = await Promise.allSettled([
    fetchDownloadResourceCategories(options.locale, firstProduct.id),
  ]);
  return buildDownloadPageData({
    productCategoriesResult,
    productId: firstProduct.id,
    resourceCategoriesResult,
  });
}

/**
 * 将路由中的产品 ID 解析为正整数。
 * @param value 路由参数中的产品 ID。
 * @returns 合法产品 ID；不合法时返回 null。
 */
export function parseDownloadProductId(value: string) {
  const productId = Number.parseInt(value, 10);

  return Number.isInteger(productId) && productId > 0 ? productId : null;
}

/**
 * 加载资料下载页所需的产品分类和下载资源数据。
 * @param options 加载参数。
 * @param options.locale 当前语言。
 * @param options.productId 当前产品 ID。
 * @returns 页面展示所需的数据和错误信息。
 */
export async function loadDownloadPageData(options: {
  locale: Locale;
  productId: number;
}): Promise<DownloadPageData> {
  const [productCategoriesResult, resourceCategoriesResult] =
    await Promise.allSettled([
      fetchDownloadProductCategories(options.locale),
      fetchDownloadResourceCategories(options.locale, options.productId),
    ]);

  return buildDownloadPageData({
    productCategoriesResult,
    productId: options.productId,
    resourceCategoriesResult,
  });
}

/** 按左侧目录的真实渲染顺序查找首个产品。 */
export function findFirstDownloadProduct(
  categories: DownloadProductCategory[],
): DownloadProduct | undefined {
  for (const category of categories) {
    const childProduct = findFirstDownloadProduct(category.children);
    if (childProduct) return childProduct;

    const product = category.products[0];
    if (product) return product;
  }

  return undefined;
}

/** 请求公开产品分类树。 */
function fetchDownloadProductCategories(locale: Locale) {
  return fetchFireflyApi<DownloadProductCategory[]>(
    "/firefly-server/product/categories",
    {
      is_show_doc: "1",
      locale,
      product_is_show_doc: "1",
      status: "1",
    },
  );
}

/** 请求指定产品的公开下载资源。 */
function fetchDownloadResourceCategories(locale: Locale, productId: number) {
  return fetchFireflyApi<DownloadResourceCategory[]>(
    "/firefly-server/product/downloads",
    {
      download_category_status: "1",
      download_post_status: "1",
      locale,
      product_id: String(productId),
    },
  );
}

/** 组合分类树和资源接口结果为页面数据。 */
function buildDownloadPageData(options: {
  productCategoriesResult: PromiseSettledResult<DownloadProductCategory[]>;
  productId: number | undefined;
  resourceCategoriesResult: PromiseSettledResult<DownloadResourceCategory[]>;
}): DownloadPageData {
  const productCategories =
    options.productCategoriesResult.status === "fulfilled"
      ? asArray<DownloadProductCategory>(options.productCategoriesResult.value)
      : [];
  const resourceCategories =
    options.resourceCategoriesResult.status === "fulfilled"
      ? asArray<DownloadResourceCategory>(options.resourceCategoriesResult.value)
      : [];
  const errors = [
    options.productCategoriesResult,
    options.resourceCategoriesResult,
  ]
    .filter(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    )
    .map((result) => getErrorMessage(result.reason));

  return {
    currentProduct:
      options.productId === undefined
        ? undefined
        : findDownloadProduct(productCategories, options.productId) ??
          buildProductFromResourceCategories(resourceCategories, options.productId),
    errors,
    productCategories,
    resourceCategories,
  };
}

/**
 * 从产品分类树中查找指定产品。
 * @param categories 产品分类树。
 * @param productId 当前产品 ID。
 * @returns 命中的产品；不存在时返回 undefined。
 */
export function findDownloadProduct(
  categories: DownloadProductCategory[],
  productId: number,
): DownloadProduct | undefined {
  for (const category of categories) {
    const product = category.products.find((item) => item.id === productId);
    if (product) return product;

    const childProduct = findDownloadProduct(category.children, productId);
    if (childProduct) return childProduct;
  }

  return undefined;
}

/**
 * 构造 Firefly 后端接口 URL。
 * @param path 接口路径。
 * @param params 查询参数。
 * @returns 可请求的完整 URL。
 */
function buildFireflyApiUrl(path: string, params: Record<string, string>) {
  const url = new URL(path, fireflyApiBaseUrl);

  for (const [key, value] of Object.entries(params)) {
    if (value !== "") {
      url.searchParams.set(key, value);
    }
  }

  return url;
}

/**
 * 请求 Firefly 后端统一响应接口。
 * @param path 接口路径。
 * @param params 查询参数。
 * @returns 接口 data 字段。
 */
async function fetchFireflyApi<T>(
  path: string,
  params: Record<string, string>,
) {
  const url = buildFireflyApiUrl(path, params);
  const response = await fetch(url, { cache: "no-store" });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${url.pathname}`);
  }

  const payload = (await response.json()) as Partial<FireflyApiResponse<T>>;
  if (payload.code !== 0) {
    throw new Error(payload.msg || `接口返回异常: ${url.pathname}`);
  }

  return payload.data as T;
}

/**
 * 将接口返回值收敛为数组。
 * @param value 接口 data 字段。
 * @returns 数组数据；非数组返回空数组。
 */
function asArray<T>(value: T[] | unknown): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * 从下载资源中兜底构造当前产品信息。
 * @param categories 下载资源分类列表。
 * @param productId 当前产品 ID。
 * @returns 可用于标题展示的产品信息；不存在时返回 undefined。
 */
function buildProductFromResourceCategories(
  categories: DownloadResourceCategory[],
  productId: number,
): DownloadProduct | undefined {
  for (const category of categories) {
    const post = category.posts.find((item) => item.product_id === productId);
    if (post) {
      return toProductFromResourcePost(post);
    }
  }

  return undefined;
}

/**
 * 将下载资源条目里的产品字段转换成产品信息。
 * @param post 下载资源条目。
 * @returns 当前产品的最小展示信息。
 */
function toProductFromResourcePost(
  post: DownloadResourcePost,
): DownloadProduct {
  return {
    buy: "",
    category_id: 0,
    code: "",
    description: "",
    href: "",
    id: post.product_id,
    list_order: 0,
    more: "",
    name: post.product_name,
    nickname: post.product_nickname,
    pdf: "",
    status: 1,
    wiki: "",
  };
}

/**
 * 提取错误对象里的可读信息。
 * @param error 捕获到的异常对象。
 * @returns 可展示的错误信息。
 */
function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "请求失败";
}
